import { Check, Pencil } from 'lucide-react';
import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type InlineEditInputProps = {
  value: string;
  onSave: (value: string) => void | Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  formatDisplay?: (value: string) => string;
  inputClassName?: string;
  placeholder?: string;
  maxLength?: number;
};

export const InlineEditInput = ({
  value,
  onSave,
  isLoading = false,
  disabled = false,
  formatDisplay,
  inputClassName = 'w-32',
  placeholder,
  maxLength,
}: InlineEditInputProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const handleSave = async () => {
    await onSave(inputValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setInputValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const displayValue = formatDisplay ? formatDisplay(value) : value;

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || disabled}
          className={inputClassName}
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={isLoading}
          className="h-8 w-8 p-0"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('btn.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{displayValue || '-'}</span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setInputValue(value);
          setIsEditing(true);
        }}
        className="h-8 w-8 p-0"
        disabled={disabled}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );
};
